import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Home, Users, Clock, Calendar, CalendarDays, DollarSign, BarChart3, Settings, ChevronRight, ChevronDown, GraduationCap } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuthStore, selectIsAdmin, selectIsManager, selectUser } from '../../stores/auth.store';
import { useMyProfile } from '../../lib/api/people.client';
/**
 * Build navigation based on user role and employment type
 */
function getNavigation(isAdmin, isManager, isExempt) {
    const nav = [
        {
            items: [
                { label: 'Dashboard', href: '/', icon: Home },
            ],
        },
    ];
    // Admin-only sections
    if (isAdmin) {
        nav.push({
            label: 'Core',
            items: [
                { label: 'People', href: '/people', icon: Users },
                {
                    label: 'Time',
                    href: '/time',
                    icon: Clock,
                    subItems: [
                        { label: 'My Punch Clock', href: '/time/punch' },
                        { label: 'Punch History', href: '/time/punch-history' },
                    ]
                },
                { label: 'Schedule', href: '/schedule', icon: CalendarDays },
                {
                    label: 'Leave',
                    href: '/leave/policies',
                    icon: Calendar,
                    subItems: [
                        { label: 'Policies', href: '/leave/policies' },
                        { label: 'All Requests', href: '/leave/admin-requests' },
                    ]
                },
            ],
        });
        nav.push({
            label: 'Management',
            items: [
                { label: 'Payroll', href: '/payroll', icon: DollarSign },
                { label: 'Reports', href: '/reports', icon: BarChart3 },
                {
                    label: 'Compliance',
                    href: '/compliance',
                    icon: GraduationCap,
                    subItems: [
                        { label: 'Curriculums', href: '/compliance' },
                        { label: 'Assignments', href: '/compliance/assignments' },
                        { label: 'Reports', href: '/compliance/reports' },
                    ]
                },
            ],
        });
        // If admin is also a manager, add approval sections
        if (isManager) {
            nav.push({
                label: 'Approvals',
                items: [
                    { label: 'Leave Requests', href: '/leave/approvals', icon: Calendar },
                    { label: 'Timesheets', href: '/time/approvals', icon: Clock },
                    { label: 'Corrections', href: '/time/corrections', icon: Clock },
                ],
            });
        }
    }
    else {
        // Manager sections (non-admin managers)
        if (isManager) {
            const teamItems = [
                { label: 'Direct Reports', href: '/team', icon: Users },
            ];
            // Only show time tracking for non-exempt (hourly) employees
            if (!isExempt) {
                teamItems.push({
                    label: 'Time',
                    href: '/time',
                    icon: Clock,
                    subItems: [
                        { label: 'My Punch Clock', href: '/time/punch' },
                        { label: 'Punch History', href: '/time/punch-history' },
                        { label: 'My Timesheets', href: '/time/timesheets' },
                        { label: 'Timesheet Approvals', href: '/time/approvals' },
                        { label: 'Correction Approvals', href: '/time/corrections' },
                    ]
                });
            }
            else {
                // Exempt employees still need to see team time data for approval
                teamItems.push({
                    label: 'Time',
                    href: '/time/punch-history',
                    icon: Clock,
                    subItems: [
                        { label: 'Punch History', href: '/time/punch-history' },
                        { label: 'Timesheet Approvals', href: '/time/approvals' },
                        { label: 'Correction Approvals', href: '/time/corrections' },
                    ]
                });
            }
            teamItems.push({ label: 'Schedule', href: '/schedule', icon: CalendarDays });
            teamItems.push({
                label: 'Leave',
                href: '/leave',
                icon: Calendar,
                subItems: [
                    { label: 'My Requests', href: '/leave/my-requests' },
                    { label: 'Approvals', href: '/leave/approvals' },
                ]
            });
            nav.push({
                label: 'Team',
                items: teamItems,
            });
            // Note: Managers do NOT have payroll access - only timesheet approval
            // Payroll is admin-only for processing final periods and exports
        }
        else {
            // Regular employee sections (non-manager, non-admin)
            const myInfoItems = [];
            // Only show time tracking for non-exempt (hourly) employees
            if (!isExempt) {
                myInfoItems.push({
                    label: 'Time',
                    href: '/time',
                    icon: Clock,
                    subItems: [
                        { label: 'Time Punch', href: '/time/punch' },
                        { label: 'My Timesheets', href: '/time/timesheets' },
                    ]
                });
            }
            myInfoItems.push({ label: 'My Schedule', href: '/my-schedule', icon: CalendarDays });
            myInfoItems.push({
                label: 'Leave',
                href: '/leave',
                icon: Calendar,
                subItems: [
                    { label: 'Balance', href: '/leave/balance' },
                    { label: 'My Requests', href: '/leave/my-requests' },
                ]
            });
            nav.push({
                label: 'My Info',
                items: myInfoItems,
            });
        }
    }
    // My Training - available to all users (everyone can have training assigned)
    nav.push({
        items: [
            { label: 'My Training', href: '/training', icon: GraduationCap },
        ],
    });
    // Settings available to everyone
    nav.push({
        items: [
            { label: 'Settings', href: '/settings', icon: Settings },
        ],
    });
    return nav;
}
export default function Sidebar({ collapsed = false }) {
    const location = useLocation();
    const isAdmin = useAuthStore(selectIsAdmin);
    const isManager = useAuthStore(selectIsManager);
    const user = useAuthStore(selectUser);
    // Fetch employee profile to determine if user is exempt (salaried)
    const { data: profileData } = useMyProfile();
    const isExempt = profileData?.employee?.employmentType === 'exempt';
    // Debug logging
    console.log('[Sidebar] User roles:', user?.roles, 'isAdmin:', isAdmin, 'isManager:', isManager, 'isExempt:', isExempt);
    const navigation = getNavigation(isAdmin, isManager, isExempt);
    console.log('[Sidebar] Navigation:', JSON.stringify(navigation.map(g => ({
        label: g.label,
        items: g.items.map(i => ({ label: i.label, href: i.href, hasSubItems: !!i.subItems?.length }))
    })), null, 2));
    const [expandedItems, setExpandedItems] = useState(new Set());
    // Auto-expand parent menu if child is active
    useEffect(() => {
        const newExpanded = new Set();
        const currentNavigation = getNavigation(isAdmin, isManager, isExempt);
        currentNavigation.forEach((group) => {
            group.items.forEach((item) => {
                if (item.subItems) {
                    const hasActiveChild = item.subItems.some(sub => location.pathname === sub.href);
                    if (hasActiveChild || location.pathname.startsWith(item.href)) {
                        newExpanded.add(item.href);
                    }
                }
            });
        });
        setExpandedItems(newExpanded);
    }, [location.pathname, isAdmin, isManager, isExempt]);
    const toggleExpanded = (href) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(href)) {
                next.delete(href);
            }
            else {
                next.add(href);
            }
            return next;
        });
    };
    return (_jsx("aside", { className: cn('fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 z-10', collapsed ? 'w-16' : 'w-64'), children: _jsx("nav", { className: "flex flex-col h-full px-3 py-4 overflow-y-auto", children: navigation.map((group, groupIdx) => (_jsxs("div", { className: cn('mb-6', groupIdx > 0 && 'pt-6 border-t border-gray-200'), children: [group.label && !collapsed && (_jsx("h3", { className: "px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider", children: group.label })), _jsx("ul", { className: "space-y-1", children: group.items.map((item) => {
                            const isActive = location.pathname === item.href ||
                                (item.href !== '/' && location.pathname.startsWith(item.href));
                            const isExpanded = expandedItems.has(item.href);
                            const hasSubItems = item.subItems && item.subItems.length > 0;
                            return (_jsx("li", { children: hasSubItems && !collapsed ? (_jsxs(_Fragment, { children: [_jsxs("button", { onClick: () => toggleExpanded(item.href), className: cn('w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors', isActive
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'), children: [_jsx(item.icon, { className: "flex-shrink-0 h-5 w-5" }), _jsx("span", { className: "flex-1 text-left", children: item.label }), item.badge && (_jsx("span", { className: "px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full", children: item.badge })), isExpanded ? (_jsx(ChevronDown, { className: "h-4 w-4" })) : (_jsx(ChevronRight, { className: "h-4 w-4" }))] }), isExpanded && item.subItems && (_jsx("ul", { className: "mt-1 ml-9 space-y-1", children: item.subItems.map((subItem) => {
                                                const isSubActive = location.pathname === subItem.href;
                                                return (_jsx("li", { children: _jsx(Link, { to: subItem.href, className: cn('block px-3 py-1.5 text-sm rounded-md transition-colors', isSubActive
                                                            ? 'bg-blue-50 text-blue-700 font-medium'
                                                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'), children: subItem.label }) }, subItem.href));
                                            }) }))] })) : (_jsxs(Link, { to: item.href, className: cn('flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors', isActive
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'text-gray-700 hover:bg-gray-100'), title: collapsed ? item.label : undefined, children: [_jsx(item.icon, { className: cn('flex-shrink-0', collapsed ? 'h-5 w-5' : 'h-5 w-5') }), !collapsed && (_jsxs(_Fragment, { children: [_jsx("span", { className: "flex-1", children: item.label }), item.badge && (_jsx("span", { className: "px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full", children: item.badge })), isActive && !hasSubItems && (_jsx(ChevronRight, { className: "h-4 w-4 text-blue-700" }))] }))] })) }, item.href));
                        }) })] }, groupIdx))) }) }));
}
